import {useState} from "react"
import {alertsService} from "@/services/api/endpoints/alertsApi"
import {AlertType, AlertActivationStatus} from "@/features/alerts/alertTypes"
import {AlertReviewAction} from "@/features/alerts/alertTypes"

interface ReviewActionParams {
  activationId: string
  action: AlertReviewAction
  comment?: string
  assignedTo?: string
}

interface UseAlertReviewActionsProps {
  setItemUpdating?: (alertId: string, isUpdating: boolean) => void
  updateAlertOptimistically?: (
    alertId: string,
    updates: Partial<AlertType>
  ) => void
  moveAlertBetweenLists?: (
    alertId: string,
    fromStatus: AlertActivationStatus,
    toStatus: AlertActivationStatus,
    updates?: Partial<AlertType>
  ) => void
  refetchInBackground?: () => Promise<void>
}

export function useAlertReviewActions({
  setItemUpdating,
  updateAlertOptimistically,
  moveAlertBetweenLists,
  refetchInBackground,
}: UseAlertReviewActionsProps = {}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>()

  const performAction = async ({
    activationId,
    action,
    comment,
    assignedTo,
  }: ReviewActionParams): Promise<boolean> => {
    try {
      setLoading(true)
      setError(undefined)

      // Set item as updating if optimistic updates are available
      setItemUpdating?.(activationId, true)

      console.log(`🔄 Starting ${action} action for alert ${activationId}`, {
        comment,
        assignedTo,
      })

      // Add the review action to the database
      console.log(`📝 Adding ${action} review to database...`)

      const reviewSuccess = await alertsService.addAlertReview(
        activationId,
        action,
        comment,
        assignedTo
      )

      if (!reviewSuccess) {
        throw new Error(`Failed to record ${action} action`)
      }
      console.log(`✅ Successfully recorded ${action} review`)

      // Update alert status if needed
      if (
        action === "resolve" ||
        action === "dismiss" ||
        action === "escalate"
      ) {
        const newStatus =
          action === "resolve"
            ? "resolved"
            : action === "dismiss"
              ? "dismissed"
              : "escalated"

        console.log(`🔄 Updating alert status to ${newStatus}...`)
        const statusSuccess = await alertsService.updateAlertStatus(
          activationId,
          newStatus
        )

        if (!statusSuccess) {
          throw new Error(`Failed to update alert status to ${action}`)
        }
        console.log(`✅ Successfully updated alert status to ${newStatus}`)

        // Use optimistic updates for status changes
        if (moveAlertBetweenLists) {
          console.log(
            `🔄 Moving alert ${activationId} to ${newStatus} status...`
          )

          // Determine current status - assume it's not resolved/dismissed if we're changing it
          const currentStatus: AlertActivationStatus =
            action === "escalate" && newStatus === "escalated"
              ? "opened" // escalating from opened
              : action === "resolve"
                ? "opened" // resolving from opened
                : action === "dismiss"
                  ? "opened" // dismissing from opened
                  : "opened" // default fallback

          console.log(`📋 Moving from ${currentStatus} to ${newStatus}`)

          moveAlertBetweenLists(activationId, currentStatus, newStatus, {
            status: newStatus,
          })

          console.log(`✅ Alert moved between lists successfully`)
        } else {
          console.log(`⚠️ moveAlertBetweenLists function not available`)
        }
      } else if (updateAlertOptimistically) {
        // For actions that don't change status (like comment, assign)
        console.log(`🔄 Updating alert ${activationId} optimistically...`)
        const updates: Partial<AlertType> = {}
        if (assignedTo) {
          // Add any relevant updates here
        }
        updateAlertOptimistically(activationId, updates)
        console.log(`✅ Alert updated optimistically`)
      }

      // Refresh data in background to sync with server
      if (refetchInBackground) {
        console.log(`🔄 Triggering background data refresh...`)
        await refetchInBackground()
        console.log(`✅ Background data refresh completed`)
      }

      return true
    } catch (err) {
      console.error(`❌ Error performing ${action} action:`, err)
      setError(err instanceof Error ? err.message : `Failed to ${action} alert`)
      return false
    } finally {
      setLoading(false)
      setItemUpdating?.(activationId, false)
    }
  }

  const resolveAlert = (activationId: string, comment?: string) =>
    performAction({activationId, action: "resolve", comment})

  const escalateAlert = (activationId: string, comment?: string) =>
    performAction({activationId, action: "escalate", comment})

  const dismissAlert = (activationId: string, comment?: string) =>
    performAction({activationId, action: "dismiss", comment})

  const commentOnAlert = (activationId: string, comment: string) =>
    performAction({activationId, action: "comment", comment})

  const assignAlert = (
    activationId: string,
    assignedTo: string,
    comment?: string
  ) => performAction({activationId, action: "assign", assignedTo, comment})

  // Reopen is handled as escalate since API doesn't support reopen
  const reopenAlert = (activationId: string, comment?: string) =>
    performAction({activationId, action: "reopen", comment})

  return {
    loading,
    error,
    resolveAlert,
    escalateAlert,
    dismissAlert,
    commentOnAlert,
    assignAlert,
    reopenAlert,
    performAction,
  }
}
